/**
 * Doctor's-note eligible items — the parent-facing read behind the special-diet
 * rows on `/family/day`.
 *
 * What used to sit alongside it (Plan-42 cases, approvals, restricted
 * components, visibility verdicts) went with that system. This module is now
 * just the sidecar: which approved special-diet items may this student order on
 * this (mealtime, serviceDate). Everything school-side lives in
 * `api/special-diet-accommodations.ts`.
 */

import { useQuery } from '@tanstack/react-query'

import { apiFetch } from './http'

export interface SpecialDietEligibleItemWire {
  menuItemId: string | null
  name: string
  menuId: string | null
  itemKind: 'BUNDLE' | 'COMPONENT' | null
  imageUri: string | null
  componentKind: string | null
  accommodationId: string
  accommodationTitle: string
  restrictedAllergens: string[]
  dietaryNeeds: string[]
  dailyCapacity: number | null
  allowMultipleOrdersPerStudent: boolean
}

interface EligibleItemsResponse {
  items: SpecialDietEligibleItemWire[]
}

export interface SpecialDietEligibleItemsQueryArgs {
  accountId: string
  studentUserId: string
  mealtime: string
  /** ISO date (YYYY-MM-DD) for the service day. */
  serviceDate: string
}

export async function fetchSpecialDietEligibleItems(
  args: SpecialDietEligibleItemsQueryArgs,
  signal?: AbortSignal,
): Promise<EligibleItemsResponse> {
  return apiFetch<EligibleItemsResponse>('/api/special-diet/eligible-items', {
    query: {
      accountId: args.accountId,
      studentUserId: args.studentUserId,
      mealtime: args.mealtime,
      serviceDate: args.serviceDate,
    },
    signal,
  })
}

export const specialDietEligibleItemsKey = (args: SpecialDietEligibleItemsQueryArgs) =>
  ['special-diet', 'eligible-items', args.accountId, args.studentUserId, args.mealtime, args.serviceDate] as const

export function useSpecialDietEligibleItemsQuery(args: SpecialDietEligibleItemsQueryArgs | null) {
  return useQuery<EligibleItemsResponse>({
    queryKey: args ? specialDietEligibleItemsKey(args) : ['special-diet', 'eligible-items', '_disabled_'],
    queryFn: ({ signal }) => fetchSpecialDietEligibleItems(args!, signal),
    enabled:
      args !== null &&
      args.accountId.length > 0 &&
      args.studentUserId.length > 0 &&
      args.mealtime.length > 0 &&
      args.serviceDate.length > 0,
    staleTime: 30_000,
  })
}
